import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, Typography } from '@mui/material';
import Authorization from '@components/Utilities/Authorization';
import { CashFlow } from '@api/CashFlows/CashFlow';
import ExpenseServ from '@api/CashFlows/ExpenseServ';
import { currencyFormatter } from '@utils/Numbers';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';

interface ModalCancelExpenseProps {
    expense?: CashFlow;
    onClose: () => void;
    onCanceled: () => void;
}

/**
 * Cancelar un gasto (plan de Gastos §5.2, vista 6): motivo obligatorio y el PIN de
 * **quien cancela** — el admin también lo teclea (`requirePin`), porque la vista pide
 * dejar registro de quién autorizó. El gasto no se borra: queda tachado y deja de
 * contar en el corte, la lista y el Estado de Resultados.
 */
const ModalCancelExpense: React.FunctionComponent<ModalCancelExpenseProps> = ({ expense, onClose, onCanceled }) => {
    const [reason, setReason] = useState('');
    const [authorized, setAuthorized] = useState(false);

    useEffect(() => {
        setReason('');
        setAuthorized(false);
    }, [expense]);

    const onConfirm = async () => {
        if (!expense) return;
        showLoader();
        try {
            const canceled = await ExpenseServ.cancelExpense(expense, reason);
            // Una compra que había entrado al inventario sale al cancelar (plan de inventario F1);
            // si el server no contestó, el gasto ya está cancelado y el aseo lo iguala mañana.
            if (canceled.inventorySyncPending) {
                showAlertWarning(
                    'Gasto cancelado; lo que había entrado al inventario sale en cuanto el servidor responda (a más tardar mañana con el aseo).'
                );
            } else {
                showAlertMessage('Gasto cancelado');
            }
            onCanceled();
            onClose();
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    return (
        <Dialog open={!!expense} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15, padding: 4 } }}>
            <DialogTitle fontWeight={600}>Cancelar gasto</DialogTitle>
            <DialogContent>
                {expense && (
                    <Stack spacing={2}>
                        <Typography>
                            {`${expense.categoryName || 'Gasto'} por ${currencyFormatter(expense.amount)}`}
                            {expense.description ? ` — ${expense.description}` : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            No se borra: queda en la lista, tachado, y deja de restar en el corte y en el Estado de Resultados.
                        </Typography>
                        <TextField
                            required
                            autoFocus
                            multiline
                            rows={2}
                            label="Motivo de la cancelación"
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                        />
                        <Divider />
                        <Authorization
                            isCurrentAccount
                            requirePin
                            onAuthorized={() => setAuthorized(true)}
                            onUnauthorized={() => setAuthorized(false)}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Volver</Button>
                <Button variant="contained" color="error" disabled={!authorized || reason.trim().length < 3} onClick={onConfirm}>
                    Cancelar gasto
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalCancelExpense;
